import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FormElement,
  FormInputsStack,
  FormDialogActions,
  FormDialogTitle,
  FormCancelButton,
  useForm,
  RichTextEditor,
} from '@marageti/z4-lib';
import { Controller, SubmitHandler, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TravelerNote } from '@marageti/z4-sdk/lib/people';

function isRichTextEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
}

const schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Subject is required')
    .max(60, 'Subject must be 60 characters or less'),
  note: z.string().refine((v) => !isRichTextEmpty(v), 'Details are required'),
});

type FormValues = z.infer<typeof schema>;

export type TravelerProfileNoteModalProps = {
  open: boolean;
  mode: 'add' | 'edit';
  note?: TravelerNote | null;
  onClose: () => void;
  onSubmit: (values: FormValues) => void;
};

const TravelerProfileNoteModal = ({
  open,
  mode,
  note,
  onClose,
  onSubmit,
}: TravelerProfileNoteModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('desktop'));
  const [hasEnteredNoteText, setHasEnteredNoteText] = useState(false);

  const {
    control,
    formState: { errors, isValid, submitCount },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      note: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && note) {
      reset({ title: note.title, note: note.content });
      setHasEnteredNoteText(!isRichTextEmpty(note.content));
    } else {
      reset({ title: '', note: '' });
      setHasEnteredNoteText(false);
    }
  }, [open, mode, note, reset]);

  const submit: SubmitHandler<FieldValues> = (data) => {
    onSubmit(data as FormValues);
    onClose();
  };

  const titleLabel = mode === 'add' ? 'Add Profile Note' : 'Edit Profile Note';
  const submitLabel = mode === 'add' ? 'Add Note' : 'Save';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <FormDialogTitle handleClose={onClose}>
        {titleLabel}
      </FormDialogTitle>

      <FormElement name={`traveler-profile-note-form-${mode}`} onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ paddingTop: 0 }}>
          <FormInputsStack>
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  value={field.value}
                  name={field.name}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  label="Subject"
                  required
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  onChange={field.onChange}
                  inputProps={{ maxLength: 60 }}
                />
              )}
            />
            <Controller
              control={control}
              name="note"
              render={({ field, fieldState }) => {
                const showNoteError = fieldState.invalid && (submitCount > 0 || hasEnteredNoteText);

                return (
                  <RichTextEditor
                    key={`${mode}-${note?.id ?? 'new'}-${open}`}
                    defaultValue={mode === 'edit' && note ? note.content : ''}
                    editorName={mode === 'add' ? 'travelerProfileNoteAdd' : 'travelerProfileNoteEdit'}
                    error={showNoteError}
                    helperText={showNoteError ? errors.note?.message as string : ''}
                    label="Details* (Add context, observations, or instructions)"
                    onChange={(v) => {
                      const normalizedValue = isRichTextEmpty(v) ? '' : v;
                      if (!hasEnteredNoteText && normalizedValue) {
                        setHasEnteredNoteText(true);
                      }
                      field.onChange(normalizedValue);
                    }}
                    sx={{
                      maxHeight: { mobile: 'calc(100vh - 256px)', desktop: 'calc(100vh - 300px)' },
                      minHeight: '270px',
                      flex: 1,
                    }}
                    useLinks
                    useLists
                  />
                );
              }}
            />
          </FormInputsStack>
        </DialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={onClose} />
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid}
            sx={{ width: { mobile: '100%', desktop: 'fit-content' } }}
          >
            {submitLabel}
          </Button>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default TravelerProfileNoteModal;
